import { useEffect, useMemo, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getServicesPaged,
  createService,
  updateService,
  deleteService,
} from '../../../../../services/servicesServices';
import {
  getBusinessLineById,
  getBusinessLinesPaged,
} from '../../../../../services/bussinesLineServices';
import ServiceCreateEditModal from './ServiceCreateEditModal';
import ConfirmModal from '../../../../../components/ConfirmModal/ConfirmModal';
import { useLanguage } from '../../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../../utils/createScopedI18n';
import { configServiceI18n } from './i18n';
import { useSnackbar } from 'context/SnackbarContext';

const FILTER_VALUES = {
  all: 'all',
  active: 'active',
  inactive: 'inactive',
};

const normalizeStatus = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (normalized === 'inactive' || normalized === 'inactivo') return 'inactive';
  return 'active';
};

const normalizeFilterValue = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (['all', 'todos'].includes(normalized)) return FILTER_VALUES.all;
  if (['active', 'activos', 'activo'].includes(normalized)) {
    return FILTER_VALUES.active;
  }
  if (['inactive', 'inactivos', 'inactivo'].includes(normalized)) {
    return FILTER_VALUES.inactive;
  }

  return null;
};

const toBackendStatusFilter = (value) => {
  switch (value) {
    case FILTER_VALUES.active:
      return 'Activos';
    case FILTER_VALUES.inactive:
      return 'Inactivos';
    default:
      return 'Todos';
  }
};

export default function ConfigService() {
  const { language } = useLanguage();
  const serviceLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configServiceI18n });
  const { showSnackbar } = useSnackbar();

  const location = useLocation();
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Button, Input, Table, Badge, IconButton, Modal, Dropdown } =
    DesignSystemComponents ?? {};

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [statusFilterValue, setStatusFilterValue] = useState(FILTER_VALUES.active);

  const [businessLines, setBusinessLines] = useState([]);
  const [loadingBusinessLines, setLoadingBusinessLines] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [formValue, setFormValue] = useState({
    id: null,
    name: '',
    businessLineId: null,
    businessLineName: '',
    status: 'active',
  });
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const [searchDebounced, setSearchDebounced] = useState('');
  const stateSearch = location.state?.search ?? '';
  const stateStatus = location.state?.status ?? '';

  useEffect(() => {
    configServiceI18n.changeLanguage(serviceLanguage);
  }, [serviceLanguage]);

  const statusOptions = useMemo(
    () => [
      { label: t('config_service_filter_all'), value: FILTER_VALUES.all },
      { label: t('config_service_filter_active'), value: FILTER_VALUES.active },
      {
        label: t('config_service_filter_inactive'),
        value: FILTER_VALUES.inactive,
      },
    ],
    [t]
  );

  const selectedStatusFilter =
    statusOptions.find((option) => option.value === statusFilterValue) ||
    statusOptions[1];

  useEffect(() => {
    if (stateSearch) {
      setSearch(stateSearch);
      setPage(0);
    }
  }, [stateSearch]);

  useEffect(() => {
    if (!stateStatus) return;
    const normalized = normalizeFilterValue(stateStatus);
    if (normalized) {
      setStatusFilterValue(normalized);
      setPage(0);
    }
  }, [stateStatus]);

  const mapSortKeyToBackend = (key) => {
    switch (key) {
      case 'name':
        return 'name';
      case 'businessLineName':
        return 'businessLineName';
      case 'status':
        return 'isEnabled';
      default:
        return null;
    }
  };

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    const normalizedDirection =
      typeof direction === 'string' ? direction.toLowerCase() : direction;
    setSortKey(normalizedDirection ? key : null);
    setSortDirection(normalizedDirection ?? null);
    setPage(0);
  };

  const backendSortKey = mapSortKeyToBackend(sortKey) || 'name';
  const backendSortDesc =
    sortDirection === null ? false : sortDirection === 'desc';

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const mapServiceRow = (service) => {
    const isEnabled =
      service.isEnabled ?? service.isenabled ?? service.enabled ?? false;
    const businessLineId =
      service.businessLineId ??
      service.businesslineId ??
      service.businessLine?.id ??
      service.businessLine?.businessLineId ??
      null;

    return {
      id: service.id,
      name: service.name || '',
      businessLineId,
      businessLineName: service.businessLine?.name || t('config_service_business_line_na'),
      status: isEnabled ? 'active' : 'inactive',
    };
  };

  useEffect(() => {
    let mounted = true;

    const fetchBusinessLines = async () => {
      try {
        setLoadingBusinessLines(true);
        const response = await getBusinessLinesPaged(1, 1000, 'Activos', null);

        if (!mounted) return;
        setBusinessLines(response.businessLines || []);
      } catch (error) {
        console.error('Error loading business lines:', error);
      } finally {
        if (mounted) {
          setLoadingBusinessLines(false);
        }
      }
    };

    fetchBusinessLines();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchServices = async () => {
      try {
        setLoading(true);

        const backendPage = page + 1;

        const response = await getServicesPaged(
          backendPage,
          pageSize,
          toBackendStatusFilter(statusFilterValue),
          searchDebounced || null,
          backendSortKey,
          backendSortDesc
        );

        if (!mounted) return;

        const mappedServices = response.services.map(mapServiceRow);

        setRows(mappedServices);
        setTotal(response.total);
      } catch (error) {
        console.error('Error loading services:', error);
        if (mounted) {
          showSnackbar(error.customMessage || t('config_service_error_load'), 'error');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchServices();

    return () => {
      mounted = false;
    };
  }, [
    page,
    pageSize,
    searchDebounced,
    backendSortKey,
    backendSortDesc,
    statusFilterValue,
    showSnackbar,
    t,
  ]);

  const statusColor = (status) =>
    normalizeStatus(status) === 'active' ? 'success' : 'light';

  const openCreate = () => {
    setFormMode('create');
    setFormValue({
      id: null,
      name: '',
      businessLineId: null,
      businessLineName: '',
      status: 'active',
    });
    setIsFormOpen(true);
  };

  const openEdit = async (row) => {
    setFormMode('edit');
    setFormValue({ ...row, status: normalizeStatus(row?.status) });
    setIsFormOpen(true);

    if (!row?.businessLineId) return;

    try {
      const line = await getBusinessLineById(row.businessLineId);
      setFormValue((prev) => ({
        ...prev,
        businessLineId: line?.id ?? prev.businessLineId,
        businessLineName: line?.name ?? prev.businessLineName,
      }));
    } catch (error) {
      console.error('Error loading business line:', error);
    }
  };

  const openDelete = (row) => {
    setToDelete(row);
    setIsDeleteOpen(true);
  };

  const reloadServices = async () => {
    const backendPage = page + 1;
    const response = await getServicesPaged(
      backendPage,
      pageSize,
      toBackendStatusFilter(statusFilterValue),
      searchDebounced || null,
      backendSortKey,
      backendSortDesc
    );

    const mappedServices = response.services.map(mapServiceRow);

    setRows(mappedServices);
    setTotal(response.total);
  };

  const saveForm = async (updatedValue) => {
    const dataToSave = updatedValue || formValue;

    if (!dataToSave.name.trim() || !dataToSave.businessLineId) {
      showSnackbar(t('config_service_required_fields'), 'error');
      return;
    }

    try {
      if (formMode === 'create') {
        await createService({
          name: dataToSave.name,
          businessLineId: dataToSave.businessLineId,
        });

        showSnackbar(t('config_service_success_created'), 'success');
      } else {
        await updateService(dataToSave.id, {
          name: dataToSave.name,
          businessLineId: dataToSave.businessLineId,
          isEnabled: normalizeStatus(dataToSave.status) === 'active',
        });

        showSnackbar(t('config_service_success_updated'), 'success');
      }

      setIsFormOpen(false);
      await reloadServices();
    } catch (error) {
      console.error('Error saving service:', error);

      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.response?.data ||
        error.customMessage ||
        t('config_service_error_save');

      showSnackbar(errorMessage, 'error');
    }
  };

  const confirmDelete = async () => {
    if (!toDelete?.id) return;

    try {
      await deleteService(toDelete.id);

      setIsDeleteOpen(false);
      setToDelete(null);

      showSnackbar(t('config_service_success_deleted'), 'success');

      await reloadServices();
    } catch (error) {
      console.error('Error deleting service:', error);

      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.response?.data ||
        error.customMessage ||
        t('config_service_error_delete');

      showSnackbar(errorMessage, 'error');
    }
  };

  if (!Button || !Input || !Table || !Badge || !IconButton || !Modal)
    return null;

  const businessLineOptions = businessLines.map((line) => ({
    label: line.name,
    value: line.id,
  }));

  return (
    <div>
      <div className='row mb-3'>
        <div className='col-5'>
          <Input
            icon='search'
            name='searchService'
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('config_service_search_placeholder')}
            type='text'
            value={search}
          />
        </div>
        <div className='col-4'>
          <Dropdown
            options={statusOptions}
            value={selectedStatusFilter}
            onChange={(option) => {
              setStatusFilterValue(option?.value || FILTER_VALUES.all);
              setPage(0);
            }}
            placeholder={t('config_service_filter_all')}
            width='100%'
            isDisabled={loading}
          />
        </div>
        <div className='col-3'>
          <div className='d-flex justify-content-end'>
            <Button
              label={t('config_service_add_button')}
              size='sm'
              variant='primary'
              onClick={openCreate}
            />
          </div>
        </div>
      </div>

      <div className='table-container'>
        <Table
          columns={[
            { accessor: 'name', label: t('config_service_col_name'), sort: true },
            {
              accessor: 'businessLineName',
              label: t('config_service_col_business_line'),
              sort: true,
            },
            {
              accessor: 'status',
              label: t('config_service_col_status'),
              sort: true,
              render: (row) => (
                <Badge
                  text={
                    normalizeStatus(row.status) === 'active'
                      ? t('config_service_status_active')
                      : t('config_service_status_inactive')
                  }
                  color={statusColor(row.status)}
                  size='sm'
                />
              ),
            },
            {
              label: t('config_service_col_actions'),
              render: (row) => (
                <div className='d-flex gap-2'>
                  <IconButton
                    ariaLabel={t('config_service_action_edit')}
                    icon='pencil'
                    onClick={() => openEdit(row)}
                    size='md'
                    iconStroke='var(--primary-500)'
                  />
                  <IconButton
                    ariaLabel={t('config_service_action_delete')}
                    icon='trash'
                    onClick={() => openDelete(row)}
                    size='md'
                    iconStroke='var(--error-500)'
                  />
                </div>
              ),
            },
          ]}
          data={rows}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          paginationMode='server'
          totalItems={total}
          paginator
          paginatorGroup={[
            { label: '10', value: 10 },
            { label: '20', value: 20 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
          ]}
          loading={loading}
          serverSortKey={sortKey}
          serverSortDirection={sortDirection}
          onServerSortChange={handleServerSortChange}
        />
        {!loading && rows.length === 0 && (
          <div className='text-center p-4'>
            <p className='caption font-light'>{t('config_service_no_results')}</p>
          </div>
        )}
      </div>

      <ServiceCreateEditModal
        Modal={Modal}
        Input={Input}
        show={isFormOpen}
        mode={formMode}
        value={formValue}
        Dropdown={Dropdown}
        onChange={setFormValue}
        onClose={() => setIsFormOpen(false)}
        onSave={saveForm}
        businessLineOptions={businessLineOptions}
        loadingBusinessLines={loadingBusinessLines}
        onRequestDelete={
          formMode === 'edit'
            ? () => {
                setIsFormOpen(false);
                setToDelete(formValue);
                setIsDeleteOpen(true);
              }
            : undefined
        }
      />

      <ConfirmModal
        Modal={Modal}
        show={isDeleteOpen}
        title={t('config_service_confirm_delete_title')}
        message={
          toDelete
            ? t('config_service_confirm_delete_message', {
                name: toDelete.name,
              })
            : t('config_service_confirm_delete_fallback')
        }
        confirmLabel={t('config_service_confirm_delete_button')}
        cancelLabel={t('config_service_cancel')}
        confirmVariant='danger'
        onConfirm={confirmDelete}
        onClose={() => {
          setIsDeleteOpen(false);
          setToDelete(null);
        }}
      />
    </div>
  );
}
